import { useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { fmtDateTime } from "./format";
import type { PatrolReview, SupervisorNote } from "../api/types";

const KIND_TEXT: Record<SupervisorNote["kind"], string> = {
  note: "Заметка",
  flag: "Отмечен: требует проверки",
  checked: "Проверено",
  annul: "Аннулирован",
};

/** Плашка решения по обходу для списков и заголовков. */
export function reviewBadge(review: PatrolReview | null) {
  switch (review) {
    case "flag":
      return <span className="badge warn">требует проверки</span>;
    case "checked":
      return <span className="badge ok">проверено</span>;
    case "annul":
      return <span className="badge danger">аннулирован</span>;
    default:
      return null;
  }
}

/**
 * Заметки и решения начальника. Запись инспектора не меняется: решение —
 * отдельная запись с автором и временем. review задан только для обхода.
 */
export function SupervisorNotes({ path, review, canWrite, onChanged }: {
  path: string;
  review?: PatrolReview | null;
  canWrite: boolean;
  onChanged?: () => void;
}) {
  const notes = useQuery<{ items: SupervisorNote[] }>(path);
  const [kind, setKind] = useState<SupervisorNote["kind"]>("note");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isPatrol = review !== undefined;
  const final = review === "annul";

  async function send(k: SupervisorNote["kind"], t: string) {
    setBusy(true);
    setError(null);
    try {
      await api.post(path, { kind: k, text: t.trim() });
      setText("");
      setKind("note");
      notes.reload();
      onChanged?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  const items = notes.data?.items ?? [];
  const needsText = kind === "note" || kind === "annul";
  return (
    <div>
      {items.length === 0 && notes.data && <p className="muted small" style={{ marginTop: 0 }}>Заметок нет.</p>}
      {items.length > 0 && (
        <ul className="plain" style={{ marginBottom: 10 }}>
          {items.map((n, i) => (
            <li key={i}>
              <div className="small muted">{fmtDateTime(n.at)} · {n.author}</div>
              <div>{n.kind !== "note" && <strong>{KIND_TEXT[n.kind]}{n.text ? ": " : ""}</strong>}{n.text}</div>
            </li>
          ))}
        </ul>
      )}
      {error && <div className="error" role="alert">{error}</div>}
      {canWrite && !final && (
        <>
          {isPatrol && (
            <div className="filters" style={{ margin: "0 0 8px" }}>
              {review !== "flag" && (
                <button className="secondary" disabled={busy} onClick={() => void send("flag", "")}>Требует проверки</button>
              )}
              {review !== "checked" && (
                <button className="secondary" disabled={busy} onClick={() => void send("checked", "")}>Проверено</button>
              )}
              <button className="danger" disabled={busy} aria-pressed={kind === "annul"}
                onClick={() => setKind(kind === "annul" ? "note" : "annul")}>Аннулировать…</button>
            </div>
          )}
          <form className="correction-form" style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}
            onSubmit={(e) => { e.preventDefault(); void send(kind, text); }}>
            <label className="field">{kind === "annul" ? "Причина аннулирования (решение окончательное)" : "Служебная заметка"}
              <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} />
            </label>
            <div className="filters" style={{ margin: 0 }}>
              <button type="submit" className={kind === "annul" ? "danger" : undefined} disabled={busy || (needsText && !text.trim())}>
                {kind === "annul" ? "Аннулировать обход" : "Добавить заметку"}
              </button>
              {kind === "annul" && <button type="button" className="secondary" onClick={() => setKind("note")}>Отмена</button>}
            </div>
          </form>
        </>
      )}
    </div>
  );
}
